import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { quotesApi } from '../services/api/quotes';
import type { CargoQuoteRequest } from '../types/quotes';
import { formatPriceRange } from '../lib/currency';
import { formatQuoteRequestDate } from '../lib/format';
import { quoteKeys } from './quoteKeys';

export interface UseQuotesListReturn {
  quotes: CargoQuoteRequest[];
  loading: boolean;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  currentPage: number;
  setCurrentPage: (page: number | ((prev: number) => number)) => void;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => void;
  paginatedItems: CargoQuoteRequest[];
  totalPages: number;
  user: { name?: string } | null;
  getPickupDate: (quote: CargoQuoteRequest) => string | null;
  getLastResponseDate: (quote: CargoQuoteRequest) => string | null;
  getPriceRange: (responses: CargoQuoteRequest['quote_responses']) => string;
}

const sortByUpdatedAt = (requests: CargoQuoteRequest[]): CargoQuoteRequest[] =>
  [...requests].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );

export const useQuotesList = (): UseQuotesListReturn => {
  const { i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language;
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [user] = useState<{ name?: string } | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const filters: Record<string, string> = statusFilter ? { status: statusFilter } : {};

  const { data: rawQuotes, isPending } = useQuery({
    queryKey: quoteKeys.list(filters),
    queryFn: () => quotesApi.getQuotes(filters),
    select: (data) => sortByUpdatedAt(data.cargo_quote_requests),
  });

  const quotes = rawQuotes ?? [];

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = quotes.slice(startIndex, endIndex);
  const totalPages = Math.ceil(quotes.length / itemsPerPage);

  // Pickup date keeps its precision in the active language: a fixed request
  // shows the exact day, a flexible (month-only) request shows the month and
  // year with no fabricated day, and an unspecified request shows nothing
  // (its caller renders a localized "no specific date" note) (#287).
  const getPickupDate = (quote: CargoQuoteRequest): string | null =>
    formatQuoteRequestDate(
      quote.date_type,
      quote.date_type === 'fixed' ? quote.origin_fixed_date : quote.origin_date,
      { language, style: 'compact' },
    );

  const getLastResponseDate = (quote: CargoQuoteRequest): string | null => {
    if (quote.quote_responses.length === 0) return null;
    const lastResponse = quote.quote_responses.reduce((latest, response) =>
      new Date(response.created_at) > new Date(latest.created_at) ? response : latest,
    );
    return formatDistanceToNow(new Date(lastResponse.created_at), { addSuffix: true });
  };

  const getPriceRange = (responses: CargoQuoteRequest['quote_responses']): string => {
    return formatPriceRange(
      responses.map((r) => ({ price: r.price, currency: r.currency })),
    );
  };

  const handleSetItemsPerPage = (count: number) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  return {
    quotes,
    loading: isPending,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage: handleSetItemsPerPage,
    paginatedItems,
    totalPages,
    user,
    getPickupDate,
    getLastResponseDate,
    getPriceRange,
  };
};
